import { CellContext } from '@tanstack/react-table';
import styled, { css } from 'styled-components';
import { TestQuestionRow } from './StepQuestionsResultsTable';

const StatusIcon = styled.i<{ className: string }>`
  margin-right: 4px;
  font-size: 12px;
`;

export const QuestionStatusPill = styled.div<{ status?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 16px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  ${({ status, theme }) => {
    switch (status) {
      case 'Answered':
        return css`
          background-color: ${theme.colors.buttonPrimary}1A;
          border: 1px solid ${theme.colors.buttonPrimary}1A;
          color: ${theme.colors.buttonPrimary};
        `;
      case 'Attempted':
        return css`
          background-color: ${theme.colors.buttonSecondary}1A;
          border: 1px solid ${theme.colors.buttonSecondary}1A;
          color: ${theme.colors.buttonSecondary};
        `;
      case 'Skipped':
        return css`
          background-color: ${theme.colors.textPrimary}1A;
          border: 1px solid ${theme.colors.textPrimary}1A;
          color: ${theme.colors.textPrimary};
        `;
      case 'Not Started':
        return css`
          background-color: ${theme.colors.textPrimary}1A;
          border: 1px solid ${theme.colors.textPrimary}1A;
          color: ${theme.colors.textPrimary};
        `;
      case 'Missing Answer':
        return css`
          background-color: ${theme.colors.textPrimary}1A;
          border: 1px solid ${theme.colors.textPrimary}1A;
          color: ${theme.colors.textPrimary};
        `;

      default:
        return css`
          background-color: ${theme.colors.textPrimary}1A;
          border: 1px solid ${theme.colors.textPrimary}1A;
          color: ${theme.colors.textPrimary};
        `;
    }
  }}
`;

export default function QuestionStatusCell(
  info: CellContext<TestQuestionRow, unknown>
) {
  const isAnswered = !!info.row.original.answer?.isAnswered;
  const isInteracted = !!info.row.original.answer?.didInteract;

  const { status, iconClass } = getQuestionStatus({
    isAnswered,
    isInteracted,
    didStartTest: info.row.original.didStartTest,
    hasAnswer: !!info.row.original.answer,
  });

  const uniqueId = `question-status-tooltip-${info.row.id}`;

  return (
    <>
      <QuestionStatusBadge
        status={status}
        iconClass={iconClass}
        uniqueId={uniqueId}
      />
    </>
  );
}
export const QuestionStatusBadge = ({
  status,
  iconClass,
  uniqueId,
}: {
  status: string;
  iconClass: string;
  uniqueId: string;
}) => (
  <QuestionStatusPill status={status} data-tooltip-id={uniqueId}>
    <StatusIcon className={iconClass} />
    {status}
  </QuestionStatusPill>
);

export function getQuestionStatus({
  isAnswered,
  isInteracted,
  didStartTest,
  hasAnswer,
}: {
  isAnswered: boolean;
  isInteracted: boolean;
  didStartTest: boolean;
  hasAnswer: boolean;
}) {
  const isSkipped = !isInteracted && hasAnswer && didStartTest;
  const isMissingAnswer = !hasAnswer && didStartTest;
  return isAnswered
    ? { status: 'Answered', iconClass: 'fa-regular fa-circle-check' }
    : isInteracted
    ? { status: 'Attempted', iconClass: 'fa-regular fa-circle-minus' }
    : isSkipped
    ? { status: 'Skipped', iconClass: 'fa-regular fa-circle-xmark' }
    : isMissingAnswer
    ? { status: 'Missing Answer', iconClass: 'fa-regular fa-circle-xmark' }
    : { status: 'Not Started', iconClass: 'fa-regular fa-circle-xmark' };
}
